export type NormalizedAuthError = {
  name?: string
  type?: string
  code?: string
  message?: string
  raw: unknown
}

type ErrorRecord = Record<string, unknown>

function asRecord(error: unknown): ErrorRecord | undefined {
  if (!error || typeof error !== 'object') return undefined
  return error as ErrorRecord
}

function normalizeErrorIdentifier(
  value: string | undefined,
): string | undefined {
  if (!value) return undefined

  const trimmedValue = value.trim()
  if (!trimmedValue) return undefined

  const separatorIndex = trimmedValue.lastIndexOf('#')
  const normalizedValue =
    separatorIndex >= 0 ? trimmedValue.slice(separatorIndex + 1) : trimmedValue

  return normalizedValue || undefined
}

function readString(
  record: ErrorRecord | undefined,
  key: string,
): string | undefined {
  const value = record?.[key]
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

export function normalizeAuthError(error: unknown): NormalizedAuthError {
  const record = asRecord(error)
  const name = normalizeErrorIdentifier(
    readString(record, 'name') ??
      (error instanceof Error && error.name ? error.name : undefined),
  )
  const type = normalizeErrorIdentifier(
    readString(record, '_type') ??
      readString(record, '__type') ??
      readString(record, 'type'),
  )
  const code = normalizeErrorIdentifier(readString(record, 'code'))
  const message =
    readString(record, 'message') ??
    (error instanceof Error && error.message ? error.message : undefined)

  return {
    name,
    type,
    code,
    message,
    raw: error,
  }
}

export function getNormalizedAuthErrorName(error: unknown): string | undefined {
  const normalized = normalizeAuthError(error)
  return normalized.name ?? normalized.type ?? normalized.code
}

export function sanitizeAuthErrorMessage(
  error: unknown,
  maxLength = 200,
): string {
  const normalized = normalizeAuthError(error)
  return (normalized.message ?? '').slice(0, maxLength).replace(/<[^>]*>/g, '')
}
